import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateStats } from '../store/slices/dashboardSlice';
import Sidebar from './Sidebar';
import Header from './Header';
import MainContentModular from './MainContentModular'; // NEW modular version

const Dashboard = () => {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { brands, total: totalBrands, search: brandSearch } = useSelector(state => state.brands);
  const { stats } = useSelector(state => state.dashboard);

  // Existing layout state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({
    competitorResearch: false,
    messages: false,
    support: false,
    settings: false
  });
  const [notifications, setNotifications] = useState([
    { id: 1, type: 'success', title: 'New affiliate approved', message: 'John Doe has been approved for your campaign', time: '2 min ago', unread: true },
    { id: 2, type: 'warning', title: 'Campaign budget alert', message: 'Campaign "Summer Sale" is 90% spent', time: '1 hour ago', unread: true },
    { id: 3, type: 'info', title: 'Weekly report ready', message: 'Your performance report is ready to view', time: '3 hours ago', unread: false },
    { id: 4, type: 'success', title: 'Payment received', message: 'Commission payment of $2,450 processed', time: '5 hours ago', unread: false }
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Static data that will eventually come from Redux
  const [recentlyContactedAffiliates] = useState([
    {
      id: 1,
      name: 'Sarah Johnson',
      email: 'sarah.j@affiliatemax.com',
      brand: 'Zodiac Casino',
      lastContact: '2 hours ago',
      status: 'responded',
      avatar: 'SJ',
      country: 'US',
      revenue: '$2,450'
    },
    {
      id: 2,
      name: 'Michael Chen',
      email: 'mchen@gamingpartners.net',
      brand: 'Sports Bet Pro',
      lastContact: '5 hours ago',
      status: 'pending',
      avatar: 'MC',
      country: 'CA',
      revenue: '$1,890'
    },
    {
      id: 3,
      name: 'Emma Rodriguez',
      email: 'emma@casinoaffs.com',
      brand: 'Lucky Slots',
      lastContact: '1 day ago',
      status: 'responded',
      avatar: 'ER',
      country: 'MX',
      revenue: '$3,120'
    }
  ]);

  const [newAffiliatesByCountry] = useState([
    { country: 'United States', code: 'US', count: 24, change: '+12%' },
    { country: 'Canada', code: 'CA', count: 18, change: '+8%' },
    { country: 'United Kingdom', code: 'GB', count: 15, change: '+15%' },
    { country: 'Australia', code: 'AU', count: 12, change: '+6%' },
    { country: 'Germany', code: 'DE', count: 9, change: '+3%' }
  ]);

  // Stats card. BrandsSection owns the actual fetch (paginated, 5 at a time).
  // - totalProjects uses the server-reported total so the count is accurate
  //   even while only the first page is loaded.
  // - The affiliate sums are derived from loaded brands and grow as the user
  //   clicks "Load more". Acceptable trade for not re-fetching all brands here.
  // - While a project search is narrowing that list, the sums are left alone:
  //   these are account totals, and they should not fall just because someone
  //   typed a brand name into the box above the cards.
  useEffect(() => {
    if (brandSearch) return;
    if (brands.length > 0 || (typeof totalBrands === 'number' && totalBrands > 0)) {
      const totalProjects = (typeof totalBrands === 'number') ? totalBrands : brands.length;
      const totalAffiliatesContacted = brands.reduce((sum, brand) => {
        return sum + (brand.live_affiliates_count || brand.liveAffiliates || 0);
      }, 0);
      const totalAffiliates = brands.reduce((sum, brand) => {
        const liveAffiliates = brand.live_affiliates_count || brand.liveAffiliates || 0;
        const untappedOpportunities = brand.untapped_opportunities || brand.untappedOpportunities || 0;
        return sum + liveAffiliates + untappedOpportunities;
      }, 0);

      dispatch(updateStats({
        totalProjects,
        totalAffiliatesContacted,
        totalAffiliates,
        newAffiliatesAdded: 23
      }));
    }
  }, [brands, totalBrands, brandSearch, dispatch]);

  const toggleMenu = (menuKey) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  return (
    <div className="min-h-screen transition-all duration-300 bg-gray-50 dark:bg-slate-950 text-sm">
      {/* Sidebar Component */}
      <Sidebar 
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        openMenus={openMenus}
        toggleMenu={toggleMenu}
      />

      {/* Mobile overlay when sidebar is open */}
      {!sidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarCollapsed(true)}
        />
      )}

      {/* Main Content Area */}
      <div className={`transition-all duration-300 ml-0 ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
        {/* Header Component */}
        <Header 
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifications={notifications}
          setNotifications={setNotifications}
          showNotifications={showNotifications}
          setShowNotifications={setShowNotifications}
          showProfileDropdown={showProfileDropdown}
          setShowProfileDropdown={setShowProfileDropdown}
          currentUser={user} // Use Redux user instead of local state
        />

        {/* NEW: Modular Main Content Component */}
        <MainContentModular 
          recentlyContactedAffiliates={recentlyContactedAffiliates}
          newAffiliatesByCountry={newAffiliatesByCountry}
        />
      </div>
    </div>
  );
};

export default Dashboard;